import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/Card";
import StatCard, { StatDelta } from "../components/StatCard";
import StackedBarChart, {
  StackSeries,
  StackDatum,
} from "../components/StackedBarChart";
import OrderTrendsChart, {
  OrderTrendDatum,
} from "../components/OrderTrendsChart";
import RechargeModal from "../payment/RechargeModal";
import { useRequest } from "../hooks/useRequest";
import {
  getDashboardSummary,
  getDashboardCredits,
  getApiTrends,
  getOrderTrends,
  getRecentCredits,
  RecentCreditActivity,
} from "../api/dashboard";
import {
  formatCents,
  formatNumber,
  formatDate,
  formatShortDate,
} from "../utils/format";
import "./Dashboard.scss";

/** 整数金额（分）→ 整美元展示，如 1250000 → "$12,500"（GMV 不展示分） */
function formatUsd(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString("en-US")}`;
}

/** 环比变化：今日 vs 昨日。昨日为 0/缺失时无法计算，返回 undefined 不展示 */
function dayOverDay(
  current?: number,
  previous?: number
): StatDelta | undefined {
  if (current == null || !previous) return undefined;
  const pct = ((current - previous) / previous) * 100;
  return {
    direction: pct >= 0 ? "up" : "down",
    text: `${Math.abs(pct).toFixed(1)}% vs yesterday`,
  };
}

/** 当月日均：当月订单数 / 当月已过天数（含今天），保留两位小数 */
function avgPerDay(monthTotal: number): string {
  const dayOfMonth = new Date().getDate();
  const avg = monthTotal / Math.max(1, dayOfMonth);
  return avg.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** API Call Trends 只统计这三种 API，堆叠展示；颜色与项目风格统一 */
const API_SERIES: StackSeries[] = [
  { key: "checkout.order", label: "Checkout Order", color: "#61a5ca" },
  { key: "product.detail", label: "Product Detail", color: "#e2e4e8" },
  { key: "product.discover", label: "Product Discover", color: "#f4866a" },
];

/** 可选的趋势天数 */
const TREND_DAY_OPTIONS = [7, 15, 30];

/** 把 recent-credits 的类型映射成展示文案 */
function activityLabel(item: RecentCreditActivity): string {
  if (item.remark) return item.remark;
  switch (item.type) {
    case "RECHARGE":
      return "Recharge";
    case "MONTHLY_RESET":
      return "Monthly reset";
    case "CONSUME":
      return "API usage";
    default:
      return item.type;
  }
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [trendDays, setTrendDays] = useState(30);
  const [orderDays, setOrderDays] = useState(30);

  useEffect(() => {
    document.title = "Dashboard | CloudStore API";
  }, []);

  const summary = useRequest(getDashboardSummary);
  const credits = useRequest(getDashboardCredits);
  // days 变化时重新请求
  const trends = useRequest(() => getApiTrends(trendDays), [trendDays]);
  const orderTrends = useRequest(() => getOrderTrends(orderDays), [orderDays]);
  const recent = useRequest(getRecentCredits);

  // 订单趋势：按日期升序，映射成组合图数据
  const orderTrendData: OrderTrendDatum[] = useMemo(() => {
    if (!orderTrends.data) return [];
    return [...orderTrends.data]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((d) => ({
        label: formatShortDate(d.date),
        orders: d.orders,
        gmvCents: d.gmv_cents,
      }));
  }, [orderTrends.data]);

  // 按日期聚合成堆叠数据：只保留 API_SERIES 里的三种 API
  const trendData: StackDatum[] = useMemo(() => {
    if (!trends.data) return [];
    const keys = new Set(API_SERIES.map((s) => s.key));
    const byDate = new Map<string, Record<string, number>>();
    trends.data.forEach((p) => {
      if (!keys.has(p.api_code)) return;
      const rec = byDate.get(p.date) || {};
      rec[p.api_code] = (rec[p.api_code] || 0) + p.calls;
      byDate.set(p.date, rec);
    });
    return Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, values]) => ({ label: formatShortDate(date), values }));
  }, [trends.data]);

  const s = summary.data;
  const c = credits.data;
  const planRemainingPct =
    c && c.planMonthlyCreditsCents > 0
      ? Math.min(100, (c.monthlyCreditsCents / c.planMonthlyCreditsCents) * 100)
      : 0;

  return (
    <div className="cs-dashboard">
      <h1 className="cs-dashboard__title">Dashboard</h1>

      {/* 统计卡片：对应 /dashboard/summary */}
      <div className="cs-dashboard__stats">
        {/* 累计订单数 */}
        <StatCard
          loading={summary.loading}
          value={s ? formatNumber(s.totalOrders) : "—"}
          label="Total Orders"
        />
        {/* 累计 GMV */}
        <StatCard
          loading={summary.loading}
          value={s ? formatUsd(s.totalGmvCents) : "—"}
          label="Total GMV"
        />
        {/* 累计商品查询次数（缺当月字段，暂不展示环比） */}
        <StatCard
          loading={summary.loading}
          value={s ? formatNumber(s.totalProductsQueried) : "—"}
          label="Total Products Queried"
        />
        {/* 今日订单数（环比：今日 vs 昨日） */}
        <StatCard
          loading={summary.loading}
          value={s ? formatNumber(s.ordersToday) : "—"}
          label="Orders Today"
          variant="primary"
          delta={dayOverDay(s?.ordersToday, s?.ordersYesterday)}
        />
        {/* 今日 GMV（环比：今日 vs 昨日） */}
        <StatCard
          loading={summary.loading}
          value={s ? formatUsd(s.gmvTodayCents) : "—"}
          label="GMV Today"
          variant="primary"
          delta={dayOverDay(s?.gmvTodayCents, s?.gmvYesterdayCents)}
        />
        {/* 当月订单数（月至今） + 日均 */}
        <StatCard
          loading={summary.loading}
          value={s ? formatNumber(s.currentMonthOrders) : "—"}
          label="Orders MTD"
          variant="primary"
          hint={s ? `Avg. ${avgPerDay(s.currentMonthOrders)}/day` : undefined}
        />
      </div>

      {/* 积分卡片：对应 /dashboard/credits */}
      <div className="cs-dashboard__credits">
        <Card>
          <div className="cs-credit__value">
            {c ? formatCents(c.monthlyCreditsCents) : "—"}
          </div>
          <div className="cs-credit__label">Plan Credits · Monthly</div>
          {/* 剩余额度进度条（宽度数据驱动，保留行内） */}
          <div className="cs-credit__bar">
            <div
              className="cs-credit__bar-fill"
              style={{ width: `${planRemainingPct}%` }}
            />
          </div>
          <div className="cs-credit__meta">
            {c
              ? `${formatCents(c.monthlyCreditsCents)} / ${formatCents(
                  c.planMonthlyCreditsCents
                )} remaining · renews ${formatDate(c.periodEnd)}`
              : ""}
          </div>
        </Card>

        <Card>
          <div className="cs-credit__head">
            <div>
              <div className="cs-credit__value cs-credit__value--success">
                {c ? formatCents(c.purchasedCreditsCents) : "—"}
              </div>
              <div className="cs-credit__label">
                Purchased Credits · Permanent
              </div>
            </div>
            <button
              type="button"
              className="cs-credit__recharge"
              onClick={() => setRechargeOpen(true)}
            >
              Recharge
            </button>
          </div>
        </Card>
      </div>

      {/* 图表行：Order Trends（留空） + Recent Credits Activity */}
      <div className="cs-dashboard__charts">
        {/* Order Trends：对应 /dashboard/order-trends，柱=订单数 + 折线=GMV */}
        <Card className="cs-dashboard__order-trends">
          <div className="cs-panel__head">
            <h2 className="cs-panel__title">Order Trends</h2>
            <div className="cs-range">
              {TREND_DAY_OPTIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`cs-range__btn${
                    orderDays === d ? " cs-range__btn--active" : ""
                  }`}
                  onClick={() => setOrderDays(d)}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
          {orderTrends.loading ? (
            <div className="cs-dashboard__chart-loading">Loading…</div>
          ) : orderTrends.error ? (
            <div className="cs-dashboard__error">{orderTrends.error}</div>
          ) : (
            <OrderTrendsChart data={orderTrendData} />
          )}
        </Card>

        {/* Recent Credits Activity：对应 /dashboard/recent-credits */}
        <Card>
          <div className="cs-panel__head cs-panel__head--tight">
            <h2 className="cs-panel__title">Recent Credits Activity</h2>
            <button
              type="button"
              className="cs-panel__viewall"
              onClick={() => navigate("/billing")}
            >
              View all →
            </button>
          </div>

          {recent.loading ? (
            <div className="cs-dashboard__muted">Loading…</div>
          ) : recent.error ? (
            <div className="cs-dashboard__error">{recent.error}</div>
          ) : (
            <table className="cs-activity">
              <thead>
                <tr className="cs-activity__thead">
                  <th className="cs-activity__th">Date</th>
                  <th className="cs-activity__th">Activity</th>
                  <th className="cs-activity__th cs-activity__th--right">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {(recent.data || []).map((item, i) => {
                  const amount = item.monthly_cents + item.purchased_cents;
                  const positive = amount >= 0;
                  return (
                    <tr key={i} className="cs-activity__row">
                      <td className="cs-activity__cell">
                        {formatDate(item.created_at)}
                      </td>
                      <td className="cs-activity__cell">
                        {activityLabel(item)}
                      </td>
                      <td
                        className={`cs-activity__amount ${
                          positive
                            ? "cs-activity__amount--positive"
                            : "cs-activity__amount--negative"
                        }`}
                      >
                        {positive ? "+" : ""}
                        {formatCents(amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {/* Paid API Usage Trends：对应 /dashboard/api-trends，三种 API 按天堆叠 */}
      <Card>
        <div className="cs-panel__head">
          <h2 className="cs-panel__title">Paid API Usage Trends</h2>
          <div className="cs-range">
            {TREND_DAY_OPTIONS.map((d) => (
              <button
                key={d}
                type="button"
                className={`cs-range__btn${
                  trendDays === d ? " cs-range__btn--active" : ""
                }`}
                onClick={() => setTrendDays(d)}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
        {trends.loading ? (
          <div className="cs-dashboard__chart-loading">Loading…</div>
        ) : trends.error ? (
          <div className="cs-dashboard__error">{trends.error}</div>
        ) : (
          <StackedBarChart series={API_SERIES} data={trendData} />
        )}
      </Card>

      {/* 充值弹窗（流程二） */}
      <RechargeModal
        open={rechargeOpen}
        onClose={() => setRechargeOpen(false)}
        balance={c}
        onSuccess={() => {
          credits.reload();
          summary.reload();
        }}
      />
    </div>
  );
};

export default Dashboard;
